// EmailDraft (TASK-496, P0) — the human-in-the-loop draft that drives the pitch (TASK-498).
// Ported from SixEase widgets/EmailDraft.tsx, re-grounded on Kielis draftApiService. A grounded
// message draft (editable subject/body) + a "Locked Facts" panel showing the exact KG facts the
// draft was built from (provenance), with Approve / Save / Send-test→Mailpit. Dual-mode: an
// optional sessionId links the draft to a transcribed meeting; otherwise it's client-scoped.
import { useCallback, useEffect, useState } from 'react';
import { Check, Copy, MailCheck, Send, ShieldAlert, ShieldCheck, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useRefreshTick } from '../../../stores/widgetRefreshStore';
import { draftApiService, type MessageDraft } from '../../../services/api/draftApiService';
import type { SignalScope } from '../../../types/signalScope';
import { WidgetContainer } from '../WidgetContainer';

function is404(err: unknown): boolean {
  const e = err as { response?: { status?: number }; message?: string };
  return e?.response?.status === 404 || Boolean(e?.message?.includes('404'));
}

/**
 * `draftId` opens ONE specific draft (TASK-702): a task artifact points at a draft id, and
 * "the client's latest draft" is a different email. Without it the review surface would show
 * the RM something other than the work they are signing off.
 */
/**
 * TASK-824: the signal identity the Radar has always passed and this widget always dropped.
 *
 * The Radar summons Email with `{clientId, subjectKey, signalGroupKey, originType,
 * originId}`; the component declared three props, so four were dropped in silence and it
 * fell through to `getLatestDraft(clientId)` — **the client's most recent draft, whatever it
 * was about**. Live, launched from an Amazon DNA conflict, it produced a generic follow-up to
 * a 29.07 meeting that never mentions Amazon or the value conflict, and committed to a
 * deadline of 15.08.2026 — ten days in the past — because an old letter was being
 * re-presented as ready to approve. One click from Approve.
 *
 * DEC-1 (answered 2026-08-25): **compose from the signal**. Not "refuse honestly" — the
 * advisor clicked Email *on a signal*, so a draft about that signal is what they asked for.
 * Shaped to TASK-829's `SignalScope` so that ticket widens this rather than rewriting it.
 */
// TASK-829: extends the shared `SignalScope`. `clientId` is optional here — this widget is
// also opened by draft id from a task artifact, where the client comes from the draft.
export interface EmailDraftProps extends Partial<SignalScope> {
  clientId?: string;
  sessionId?: string;
  draftId?: string;
  /**
   * What the signal SAYS, in one line, built by the caller from what it already renders.
   * It becomes the composer's `topic` — the route appends "Focus of this email: {topic}" to
   * its DNA-grounded summary and **422s a follow-up that has neither facts nor a topic**, so
   * this is the grounding, not a label.
   */
  signalSummary?: string;
}

export function EmailDraft({
  clientId,
  sessionId,
  draftId,
  subjectKey,
  signalSummary,
}: EmailDraftProps) {
  // The RM's working language — what the draft is composed in (the request used to carry none).
  const { i18n } = useTranslation();
  // When the agent writes a draft ("…in English"), this tile is already mounted with the same
  // props, so nothing would re-read it and the advisor keeps looking at the previous draft
  // while the reply says the new one is ready (live, 2026-08-24).
  const draftTick = useRefreshTick('draft_email', 'send_email');
  const [draft, setDraft] = useState<MessageDraft | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  // TASK-824: `signal` — opened from a signal, nothing composed yet. Deliberately NOT
  // `empty`: "no draft yet for this client" is a statement about the client, and answering it
  // by showing the client's last unrelated letter is what this ticket exists to stop.
  const [status, setStatus] = useState<'loading' | 'empty' | 'error' | 'ready' | 'signal'>('loading');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentUrl, setSentUrl] = useState<string | null>(null);

  const apply = useCallback((d: MessageDraft) => {
    setDraft(d);
    setSubject(d.subject ?? '');
    setBody(d.draft_text ?? '');
    setStatus('ready');
    setSentUrl(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!draftId && !clientId) {
      setStatus('error');
      setMessage('No draft or client was given.');
      return;
    }
    // TASK-824: opened FROM A SIGNAL, this widget never falls back to the client's latest
    // draft. That fallback is the whole defect — it produced a plausible, confident letter
    // about a different subject, with a commitment whose deadline had already passed, one
    // click from Approve. With a `subjectKey` in hand the widget composes about the signal
    // instead (see `composeForSignal` below); it never RETRIEVES something else and presents
    // it as this signal's work.
    if (subjectKey && !draftId) {
      setStatus('signal');
      return;
    }
    setStatus('loading');
    setSentUrl(null);
    const load = draftId
      ? draftApiService.getDraft(draftId)
      : draftApiService.getLatestDraft(clientId as string);
    load
      .then((d) => {
        if (!cancelled) apply(d);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (is404(err)) setStatus('empty');
        else {
          setStatus('error');
          setMessage(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
    // `draftTick` re-reads the latest draft when a turn wrote one.
  }, [clientId, draftId, apply, draftTick, subjectKey]);

  const dirty = draft != null && (subject !== (draft.subject ?? '') || body !== (draft.draft_text ?? ''));

  // Compose in the language the RM is working in. The draft used to come back in German
  // whatever was asked, because the request carried no language at all.
  const generate = async (opts?: { purpose?: 'follow_up' | 'information_request' }) => {
    // Opened by draft id, the client comes from the loaded draft — generating still needs one.
    const forClient = draft?.client_id ?? clientId;
    if (!forClient) return;
    setBusy(true);
    try {
      const d = await draftApiService.generateDraft(forClient, {
        session_id: sessionId,
        // Omitted rather than sent as undefined when i18n has not resolved one — the API
        // contract is "absent = the server default".
        ...(i18n.language ? { language: i18n.language } : {}),
        purpose: opts?.purpose ?? 'follow_up',
      });
      apply(d);
    } catch (err) {
      setStatus('error');
      // A 422 here is the server refusing to invent a letter — its message names the email
      // that CAN be written, so it must reach the RM verbatim rather than as "failed".
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * TASK-824: compose a draft ABOUT THIS SIGNAL.
   *
   * `signalSummary` becomes the composer's `topic`, which the route appends as "Focus of
   * this email:" to its DNA-grounded summary. This is the supported grounding path, not a
   * second composer — and the route 422s a follow-up with neither facts nor a topic, so a
   * signal that carries no summary correctly produces a refusal rather than a generic letter.
   */
  const composeForSignal = async () => {
    if (!clientId || !signalSummary) return;
    setBusy(true);
    try {
      const d = await draftApiService.generateDraft(clientId, {
        session_id: sessionId,
        ...(i18n.language ? { language: i18n.language } : {}),
        purpose: 'follow_up',
        topic: signalSummary,
      });
      apply(d);
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Action failures (e.g. a read-only session 403) surface as a toast and keep the
  // composer editable — never an unhandled rejection / dead widget.
  const actionError = (action: string, err: unknown) =>
    toast.error(`${action} failed: ${err instanceof Error ? err.message : String(err)}`);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      apply(await draftApiService.patchDraftText(draft.id, { subject, draft_text: body }));
    } catch (err) {
      actionError('Save', err);
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      if (dirty) await draftApiService.patchDraftText(draft.id, { subject, draft_text: body });
      apply(await draftApiService.approveDraft(draft.id));
    } catch (err) {
      actionError('Approve', err);
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const res = await draftApiService.sendTestDraft(draft.id);
      setSentUrl(res.mailhog_ui);
      setDraft({ ...draft, status: res.status });
    } catch (err) {
      actionError('Send test', err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <WidgetContainer source={sessionId ? 'Meeting' : 'Knowledge Graph'}>
      {status === 'loading' && <div className="h-64 animate-pulse rounded-xl bg-accent" />}

      {status === 'error' && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load the draft.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">{message}</p>
        </div>
      )}

      {status === 'signal' && (
        <div data-testid="email-draft-signal" className="flex flex-col items-start gap-3 py-6">
          {signalSummary ? (
            <>
              <p className="text-sm text-muted-foreground">
                No draft has been written about this signal yet.
              </p>
              <p
                data-testid="email-draft-signal-summary"
                className="rounded-md border border-border bg-muted/40 px-3 py-2 text-[13px] text-foreground"
              >
                {signalSummary}
              </p>
              <button
                type="button"
                data-testid="email-draft-compose-signal"
                onClick={() => composeForSignal()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
                {busy ? 'Composing…' : 'Compose about this signal'}
              </button>
            </>
          ) : (
            // The caller gave no evidence to compose from. Say so — do NOT reach for the
            // client's latest draft, which is the defect, and do not invent a subject.
            <p className="text-sm text-muted-foreground">
              This signal carries no summary to compose from, so there is nothing to write
              yet. Open the signal to see its evidence.
            </p>
          )}
        </div>
      )}

      {status === 'empty' && (
        <div className="flex flex-col items-start gap-3 py-6">
          <p className="text-sm text-muted-foreground">No draft yet for this client.</p>
          <button
            type="button"
            onClick={() => generate()}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" />
            {busy ? 'Generating…' : 'Generate grounded draft'}
          </button>
        </div>
      )}

      {/* TASK-656: fail-closed scope state — the server withholds the content of
          an unverified draft; the widget shows only the verdict and the recovery
          path. No composer, no facts, no approve/send. */}
      {status === 'ready' && draft && draft.scope_status === 'unverified' && (
        <div
          role="alert"
          data-testid="draft-scope-unverified"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[13px] text-amber-900"
        >
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-semibold">Source scope could not be verified</p>
            <p className="mt-0.5">
              The latest draft was withheld because its sources could not be verified as
              this client's own. It is blocked from approval and sending — regenerate to
              rebuild the email from verified client facts.
            </p>
            <button
              type="button"
              onClick={() => generate()}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {busy ? 'Regenerating…' : 'Regenerate draft'}
            </button>
          </div>
        </div>
      )}

      {status === 'ready' && draft && draft.scope_status !== 'unverified' && (
        <div className="flex flex-col gap-4 lg:flex-row" data-testid="email-draft-widget">
          {/* Composer */}
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[15px] font-semibold text-foreground">Email Composer</span>
              <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {draft.status}
              </span>
            </div>

            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Subject"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
            />
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm leading-relaxed text-foreground outline-none focus:ring-1 focus:ring-primary"
            />

            {/* TASK-656: coherent lifecycle — a Sent draft never re-offers
                Approve/Send as if it were new. */}
            {draft.status === 'sent' ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="draft-sent-state">
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700">
                  <MailCheck className="h-4 w-4" />
                  Sent
                  {draft.sent_at ? ` · ${new Date(draft.sent_at).toLocaleString()}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(`${subject}\n\n${body}`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
                <button
                  type="button"
                  onClick={() => generate()}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <Sparkles className="h-4 w-4" />
                  {busy ? 'Generating…' : 'Start new draft'}
                </button>
                {sentUrl && (
                  <a href={sentUrl} target="_blank" rel="noreferrer" className="text-[12px] text-primary underline">
                    View in Mailpit →
                  </a>
                )}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={approve}
                  disabled={busy || draft.status === 'approved'}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:opacity-50"
                >
                  <Check className="h-4 w-4" />
                  {draft.status === 'approved' ? 'Approved' : 'Approve'}
                </button>
                {dirty && (
                  <button
                    type="button"
                    onClick={save}
                    disabled={busy}
                    className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                  >
                    Save edits
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(`${subject}\n\n${body}`)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                  Send test
                </button>
                {sentUrl && (
                  <a href={sentUrl} target="_blank" rel="noreferrer" className="text-[12px] text-primary underline">
                    View in Mailpit →
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Locked Facts (provenance) */}
          <div className="w-full shrink-0 lg:w-[280px]">
            <div className="rounded-xl border border-border bg-muted/40 p-3">
              <h3 className="mb-3 flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
                <ShieldCheck className="h-4 w-4 text-emerald-600" />
                Locked Facts
              </h3>
              {draft.provenance.length ? (
                <ul className="space-y-3">
                  {draft.provenance.map((p, i) => (
                    <li key={`${p.fact_key}-${i}`} className="text-sm">
                      <p className="text-foreground">{p.value}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                        {p.fact_key} · {p.source}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No grounded facts on this draft.</p>
              )}
              {/* TASK-656: excluded-fact disclosure (keys only, never the fact). */}
              {draft.scope_status === 'verified' && draft.scope_note && (
                <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
                  {draft.scope_note}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </WidgetContainer>
  );
}
